import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useObserveContainerSize } from '../core/hooks/useObserveContainerSize.js';
import { ChartType } from '../core/types/chart-types.js';
import type { ComponentWithName } from '../core/types/component-with-name.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import { GaugeChartLayout } from './components/GaugeChartLayout/GaugeChartLayout.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  GAUGE_CHART_ARIA_LABEL,
} from './constants.js';
import { useGaugeChartConfig } from './hooks/useGaugeChartConfig.js';
import { GaugeChartProviders } from './providers/GaugeChartProviders.js';
import { Label } from './slots/Label.js';
import { Threshold } from './slots/Threshold.js';
import type { GaugeChartConsumerConfig } from './types/gauge-chart-config.js';
import type { GaugeChartProps, GaugeChartRef } from './types/gauge-chart.js';

const _GaugeChart = forwardRef<
  GaugeChartRef,
  PropsWithChildren<GaugeChartProps>
>((props, forwardedRef) => {
  const {
    value,
    width,
    height,
    style,
    className,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'gauge-chart-root',
  } = props;

  const config = useGaugeChartConfig(props);
  const { min, max, color, formatter, thresholds } = config;

  const chartWidth = coerceSizeValue(width) ?? DEFAULT_CHART_WIDTH;
  const chartHeight = coerceSizeValue(height) ?? DEFAULT_CHART_HEIGHT;

  const [{ height: containerHeight, width: containerWidth }, containerRef] =
    useObserveContainerSize();

  const gaugeChartRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => {
    const consumerConfig: GaugeChartConsumerConfig = {
      min,
      max,
      color,
      formatter,
      thresholds,
    };

    return {
      element: gaugeChartRef.current,
      getConfig: () =>
        JSON.stringify({ type: ChartType.GaugeChart, ...consumerConfig }),
    };
  });

  const ref = useMergeRefs<GaugeChartRef | HTMLDivElement>([
    forwardedRef,
    gaugeChartRef,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        width: chartWidth,
        height: chartHeight,
        overflow: 'hidden',
        ...style,
      }}
      className={className}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
      role="img"
      aria-label={GAUGE_CHART_ARIA_LABEL}
    >
      <GaugeChartProviders
        config={config}
        width={containerWidth}
        height={containerHeight}
      >
        <GaugeChartLayout ref={ref} value={value} color={color} />
      </GaugeChartProviders>
    </div>
  );
});

(_GaugeChart as ComponentWithName<typeof _GaugeChart>).displayName =
  'GaugeChart';

/**
 * A Gauge Chart is a circular chart that displays information on a numeric
 * scale.
 * It's great for showing performance metrics or progress towards a goal.
 *
 * @public
 */
export const GaugeChart = Object.assign(_GaugeChart, {
  Label,
  Threshold,
});
